import React, {useEffect} from 'react'
import CheckoutView from './checkout/CheckoutView'
import SearchView from './search/SearchView'
import {useGetConfigQuery} from '../services/ibeApi'
import {AppView, IbeConfig} from '../types'
import {useAppDispatch, useAppSelector} from '../store/hooks'
import {
  selectAppView,
  selectIsSearchView,
  setAppView
} from '../store/slices/appViewSlice'
import ShoppingCartModal from './cart/ShoppingCartModal'
import useInitSearchParams from '../hooks/useInitSearchParams'
import useCartValidator from '../hooks/useCartValidator'
import ShoppingCartButton from './cart/ShoppingCartButton'
import ShoppingCartItemEditorModal from './cart/ShoppingCartItemEditorModal'
import ToastNotificationManager from './common/ToastNotificationManager'
import {setConfig} from '../store/slices/configSlice'
import {initAuthState, initAuthListener} from '../services/auth'
import ConfirmationView from './confirmation/ConfirmationView'
import {getConfig} from '../services/config'
import {getLogger} from '../services/logging'
import NavBar from './nav/NavBar'
import {
  selectSearchParams,
  setSearchParams
} from '../store/slices/searchParamsSlice'
import Alert from './common/Alert'
import ShoppingCartValidationModal from './cart/ShoppingCartValidationModal'
import {initializeGA4, pushDataLayerEvent} from '../services/dataLayer'
import {selectSessionId, setSessionId} from '../store/slices/sessionSlice'
import {generateUUID} from '../services/utils'

const logger = getLogger('App')

const TIMED_VALIDATION_INTERVAL_MS = 1000 * 60 * 15 // 15 minutes
const INITIAL_VALIDATION_DELAY_MS = 1000 * 3 // 3 seconds

const App = () => {
  useInitSearchParams()
  const {validate} = useCartValidator()
  const dispatch = useAppDispatch()
  const appView = useAppSelector(selectAppView)
  const searchParams = useAppSelector(selectSearchParams)
  const isSearchView = useAppSelector(selectIsSearchView)
  const sessionId = useAppSelector(selectSessionId)
  const configQuery = useGetConfigQuery(null)
  const config = configQuery.data as IbeConfig

  useEffect(() => {
    // initialize Google Analytics
    const {googleAnalyticsId} = getConfig()
    logger.info({
      message: `Initializing Google Analytics...`,
      data: {googleAnalyticsId}
    })
    initializeGA4(googleAnalyticsId)

    // initialize AWS Amplify Auth
    initAuthState(dispatch)
    const unsubscribeAuthListener = initAuthListener(dispatch)
    return () => {
      unsubscribeAuthListener()
    }
  }, [])

  useEffect(() => {
    // initialize session ID
    if (!sessionId) {
      const newSessionId = generateUUID()
      logger.info({
        message: 'Generating new session ID...',
        data: {sessionId: newSessionId}
      })
      dispatch(setSessionId(newSessionId))
    }
  }, [sessionId])

  useEffect(() => {
    if (
      (searchParams?.location === 'ALL' || !searchParams?.location?.length) &&
      appView !== AppView.HOME
    ) {
      logger.info({
        message: 'Redirecting to home view...',
        data: {appView, searchParams}
      })
      dispatch(setAppView(AppView.HOME))
    }
  }, [])

  useEffect(() => {
    pushDataLayerEvent({
      event: `App_View_${appView}`,
      category: 'IBE',
      value: 1
    })

    // scroll on view changes
    if (
      appView === AppView.CHOOSE_PROPERTY ||
      appView === AppView.CHOOSE_UNIT
    ) {
      // scroll to search results when on search view
      setTimeout(() => {
        const searchResultsElement = document.getElementById(
          'ibe-search-results'
        )
        if (searchResultsElement) {
          const NAV_HEIGHT = 100
          window.scrollTo({
            top: searchResultsElement.offsetTop - NAV_HEIGHT,
            behavior: 'smooth'
          })
        }
      }, 100)
    } else {
      // scroll up when view changes
      window.scrollTo(0, 0)
    }
  }, [appView, searchParams])

  useEffect(() => {
    if (config) {
      dispatch(setConfig(config))
    }
  }, [configQuery])

  // trigger cart validation on initial load
  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      logger.info({
        message: 'Running initial cart validation...',
        data: {timestamp: new Date().toISOString()}
      })
      validate()
    }, INITIAL_VALIDATION_DELAY_MS)

    // clear timeout on unmount
    return () => {
      logger.info({
        message: 'Clearing initial cart validation timeout...'
      })
      clearTimeout(timeoutRef)
    }
  }, [])

  // trigger cart validation on timed interval
  useEffect(() => {
    logger.info({
      message: `Starting validation interval at ${TIMED_VALIDATION_INTERVAL_MS} ms`,
      data: {timestamp: new Date().toISOString()}
    })
    const intervalRef = setInterval(() => {
      logger.info({
        message: 'Running repeating cart validation on timer...',
        data: {timestamp: new Date().toISOString()}
      })
      validate()
    }, TIMED_VALIDATION_INTERVAL_MS)

    // clear interval on unmount
    return () => {
      logger.info({
        message: 'Clearing timed cart validation interval...'
      })
      clearInterval(intervalRef)
    }
  }, [])

  // Helper function to call parent callback
  const callParentCallback = () => {
    const appRoot = document.getElementById('appRoot')
    const onStateChangeName = appRoot?.getAttribute('on_state_change')
    if (onStateChangeName && typeof window !== 'undefined') {
      const onStateChangeFunc = (window as any)[onStateChangeName]
      if (typeof onStateChangeFunc === 'function') {
        onStateChangeFunc(appView)
      }
    }
  }

  // Call parent callback on mount
  useEffect(() => {
    callParentCallback()
  }, [])

  // Call parent callback when appView changes
  useEffect(() => {
    callParentCallback()
  }, [appView])

  const currentDate = new Date()
  return (
    <div className="ibe-hbml placemakr-ibe" data-theme="placemakr">
      <div className="ibe-body bg-base-100">
        <main className="w-full">
          <section className="p-4 sm:p-8 max-w-screen-xl mx-auto">
            {configQuery.isLoading && (
              <div className="flex flex-col gap-8 my-4">
                <div className="skeleton h-20 rounded-full w-full"></div>
                <div className="skeleton h-16 rounded-full w-1/2 mx-auto"></div>
                <div className="skeleton h-96 w-full"></div>
              </div>
            )}
            {!configQuery.isLoading && config && (
              <>
                {/* Toast Notifications */}
                <ToastNotificationManager />

                {/* Shopping Cart Button in NavBar */}
                <ShoppingCartButton targetSelector="#nav_booking_btn" />

                {/* Search View */}
                {isSearchView && <SearchView />}

                {/* Checkout View */}
                {appView === AppView.CHECKOUT && <CheckoutView />}

                {/* Confirmation View */}
                {appView === AppView.CONFIRMATION && <ConfirmationView />}

                {/* Shopping Cart */}
                <ShoppingCartModal />

                {/* Shopping Cart Item Editor */}
                <ShoppingCartItemEditorModal />

                {/* Shopping Cart Validation Errors */}
                <ShoppingCartValidationModal />
              </>
            )}
            {!configQuery.isLoading && !config && (
              <Alert type="error" className="p-8">
                An application error occurred. Configuration could not be
                loaded. If the problem persists, please contact support:
                <a
                  className="link"
                  target="_blank"
                  href="mailto:hello@placemakr.com"
                >
                  hello@placemakr.com
                </a>
              </Alert>
            )}
          </section>
        </main>
      </div>
    </div>
  )
}

export default App
